import { ISortByQuery } from '@common/api.models';
import { DEFAULT_TABLE_PAGE_SIZE_OPTIONS } from '@common/constants';
import { SearchFilter } from '@common/searchFilter/SearchFilter';
import { IFilterFieldConfig, SearchFilterType } from '@common/searchFilter/searchFilter.models';
import { SearchPageBase } from '@common/SearchPageBase';
import {
  createTableDefaultColumn,
  createTableLinkWithOpenNewTabColumn,
  createTableUserRoleColumn,
} from '@common/tableColumn.services';
import {
  Column,
  ComponentType,
  createTableCell,
  Grid,
  Icon,
  IconName,
  createTableColumn,
  IResponsivePaginatedTableProps,
  useMediaQuery,
  TableCellExtras,
  ResponsivePaginatedTable,
} from '@gcl/react-core';
import { routes } from '@routes/routes.models';
import {
  useFetchSrpOptionsWithSearch,
  useFetchTwcmOptionsWithSearch,
  useFetchTwcOptionsWithSearch,
} from '@userProfile/userProfile.queries';
import * as React from 'react';
import {
  EPermitToLoadStatus,
  ILoadingRemovalCertSearch,
  ILoadingRemovalCertSearchFilter,
  loadingRemovalCertSearchColumnKeys,
  loadingRemovalCertSearchKeys,
} from './permitToLoadRemove.models';
import { useFetchLoadingRemovalCertSearch } from './permitToLoadRemove.queries';
import './PermitToLoadRemoveList.scss';
import { useNavigate } from '@routes/routes.hooks';
import { useProjectId } from '@config/config.hooks';
import { useSelectT1CreationModal } from '@common/SelectT1CreationModal.hooks';
import { ESelectT1CreationModalType } from '@common/SelectT1CreationModal.constants';
import { useGetMasterScheduleOptionsForLoadingRemovalCert } from '@masterSchedule/masterSchedule.queries';
import { BooleanParam, NumberParam, StringParam } from 'use-query-params';
import { getSelectFilterFieldNameId } from '@common/searchFilter/searchFilter.services';
import { useSearchFilterUrlParam } from '@common/searchFilter/searchFilterUrlParam.hooks';
import { useFetchAllRiskCategories } from '@riskCategory/riskCategory.queries';
import { DownloadButton } from '@file/DownloadButton';
import { IButtonNavBarItem } from '@app/ButtonNavBar';
import { useUserProfilePreferencesPageSize } from '@userProfile/userProfilePreferences.hooks';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { permitToLoadRemoveActionerOptions } from './permitToLoadRemoveOptions.models';
import { useAllowNewT4 } from './permitToLoadRemove.hooks';
import {
  exportProjectLoadingRemovalCertSearchExcelAsync,
  exportProjectLoadingRemovalCertSearchPdfAsync,
} from './permitToLoadRemove.services';
import '@common/Common.scss';

export interface IPermitToLoadRemoveListProps {}

export const PermitToLoadRemoveList: React.FunctionComponent<IPermitToLoadRemoveListProps> = ({}) => {
  const isXs = useMediaQuery('(max-width: calc(640px))');
  const DEFAULT_SORT_BY_FIELD_NAME = loadingRemovalCertSearchColumnKeys.CERTIFICATE_NO;

  const { initPageSize, pageSize, setPageSize } = useUserProfilePreferencesPageSize(
    EUserProfilePreferenceAttributeName.T4_LIST_PAGE_SIZE,
  );
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [sortByQuery, setSortByQuery] = React.useState<ISortByQuery | undefined>({
    sortByField: loadingRemovalCertSearchKeys.CERTIFICATE_NO,
    isDesc: true,
  });
  const projectId = useProjectId();
  const { navigate, generateRoutePath, projectNo } = useNavigate();
  const isAllowNewT4 = useAllowNewT4();
  // #region Search Field Configs
  const permitToLoadRemoveSearchFieldConfigs = React.useMemo(() => {
    return [
      {
        fieldName: loadingRemovalCertSearchKeys.CERTIFICATE_NO,
        displayName: 'T4 No.',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.ACTIONER,
        displayName: 'Actioner',
        type: SearchFilterType.TextSelect,
        textSelectOptions: permitToLoadRemoveActionerOptions,
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.RISK_CATEGORY,
        displayName: 'Risk Category',
        type: SearchFilterType.Select,
        useFetchOptions: useFetchAllRiskCategories({ shouldConvertOptions: true }),
        useFetchOptionsProps: {},
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.T1_REF_NO,
        displayName: 'T1',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,

      {
        fieldName: loadingRemovalCertSearchKeys.DESIGN_PACKAGE,
        displayName: 'Design Package',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.LOCATION,
        displayName: 'Location',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,

      {
        fieldName: loadingRemovalCertSearchKeys.ELEMENT_OF_WORKS,
        displayName: 'Element of Works',
        type: SearchFilterType.String,
      } as IFilterFieldConfig,
      // {
      //   fieldName: loadingRemovalCertSearchKeys.IS_REMOVAL_CERT,
      //   displayName: 'Is Removal Cert',
      //   type: SearchFilterType.Boolean,
      // } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.REMOVAL_REQUIRED,
        displayName: 'Removal Required',
        type: SearchFilterType.Boolean,
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.TWC,
        displayName: 'TWC',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.TWCM,
        displayName: 'TWCM',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchTwcmOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
      {
        fieldName: loadingRemovalCertSearchKeys.SRP,
        displayName: 'SRP',
        type: SearchFilterType.SelectWithSearch,
        useFetchOptionsWithSearch: useFetchSrpOptionsWithSearch({ shouldConvertOptions: true }),
        useFetchOptionsProps: { projectId },
      } as IFilterFieldConfig,
    ];
  }, []);
  // #endregion Search Field Configs

  const { localFilter, setLocalFilter } = useSearchFilterUrlParam(
    {
      [loadingRemovalCertSearchKeys.CERTIFICATE_NO]: StringParam,
      [loadingRemovalCertSearchKeys.LOCATION]: StringParam,
      [loadingRemovalCertSearchKeys.ELEMENT_OF_WORKS]: StringParam,
      [loadingRemovalCertSearchKeys.ACTIONER]: StringParam,
      [loadingRemovalCertSearchKeys.IS_REMOVAL_CERT]: BooleanParam,
      [loadingRemovalCertSearchKeys.REMOVAL_REQUIRED]: BooleanParam,
      [loadingRemovalCertSearchKeys.T1_REF_NO]: StringParam,
      [loadingRemovalCertSearchKeys.DESIGN_PACKAGE]: StringParam,
      [`${getSelectFilterFieldNameId(loadingRemovalCertSearchKeys.RISK_CATEGORY)}`]: NumberParam,
      [loadingRemovalCertSearchKeys.TWC]: StringParam,
      [loadingRemovalCertSearchKeys.TWCM]: StringParam,
      [loadingRemovalCertSearchKeys.SRP]: StringParam,
    },
    permitToLoadRemoveSearchFieldConfigs,
  );

  const loadingRemovalCertFilterObj = React.useMemo(() => {
    const temp = {} as any;

    Object.keys(localFilter).forEach((key) => {
      if (!key.includes('.')) temp[key] = localFilter[key];
      else {
        const splitedKey = key.split('.');
        const child = localFilter[splitedKey[0]] ?? {};
        child[splitedKey[1]] = localFilter[key];
        temp[splitedKey[0]] = child;
      }
    });

    return temp as ILoadingRemovalCertSearchFilter;
  }, [JSON.stringify(localFilter)]);

  const {
    renderModal: renderLoadingRemovalCertCreationModal,
    openPortal: openLoadingRemovalCertCreationModal,
    closePortal: closeLoadingRemovalCertCreationModal,
  } = useSelectT1CreationModal({
    type: ESelectT1CreationModalType.T4,
    fetchT1OptionsFn: useGetMasterScheduleOptionsForLoadingRemovalCert,
    fetchT1OptionFnProps: { projectId },
  });

  const {
    data: designBriefPagedResult,
    isLoading: isLoadingDesignBriefSearch,
    isRefetching: isRefetchingDesignBriefSearch,
    error: errorDesignBriefSearch,
  } = useFetchLoadingRemovalCertSearch({
    pageSize,
    pageNumber,
    projectId,
    ...(sortByQuery || {}),
    filter: Object.keys(localFilter).length > 0 ? (localFilter as ILoadingRemovalCertSearchFilter) : undefined,
  });

  // #region Table Columns

  const getT4ActionerClassName = (t4: ILoadingRemovalCertSearch): string | undefined => {
    switch (t4.actioner) {
      case '-':
        return 'no-action';
      case 'REMOVAL CERT':
        return 'removal-cert';
      default:
        return t4.actioner?.toLowerCase();
    }
  };

  const permitToLoadRemoveTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableLinkWithOpenNewTabColumn<ILoadingRemovalCertSearch>(
        loadingRemovalCertSearchColumnKeys.CERTIFICATE_NO,
        'T4 No.',
        ({ item }) => {
          return generateRoutePath(routes.t4.view.path, { id: item.id.toString() });
        },
        ({ value, item, index, cellExtras }, link) => {
          let statusIcon = (
            <Icon iconName="RecordCircle" className="gcl-gems-design-modification-list-not-released-icon gcl-ml-xs" />
          );
          if (
            !item.isRemovalCert &&
            (item.status === EPermitToLoadStatus.WAITING_FOR_ENG_REMOVAL ||
              item.status === EPermitToLoadStatus.WAITING_FOR_TWC_REMOVAL ||
              item.status === EPermitToLoadStatus.TWC_REMOVAL_REJECTED)
          ) {
            statusIcon = (
              <>
                <Icon
                  iconName="CheckCircleFill"
                  className="gcl-gems-design-modification-list-released-icon gcl-ml-xs"
                />
                <Icon
                  iconName="RecordCircle"
                  className="gcl-gems-design-modification-list-not-released-icon gcl-ml-xs"
                />
              </>
            );
          }

          if (item.status === EPermitToLoadStatus.COMPLETED && item.removalCertRequired)
            statusIcon = (
              <>
                <Icon
                  iconName="CheckCircleFill"
                  className="gcl-gems-design-modification-list-released-icon gcl-ml-xs"
                />
                <Icon
                  iconName="CheckCircleFill"
                  className="gcl-gems-design-modification-list-released-icon gcl-ml-xs"
                />
              </>
            );
          if (item.status === EPermitToLoadStatus.COMPLETED && item.isRemovalCert)
            statusIcon = (
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-modification-list-released-icon gcl-ml-xs" />
            );
          if (item.status === EPermitToLoadStatus.COMPLETED && !item.removalCertRequired)
            statusIcon = (
              <Icon iconName="CheckCircleFill" className="gcl-gems-design-modification-list-released-icon gcl-ml-xs" />
            );
          if (item.status === EPermitToLoadStatus.CANCEL)
            statusIcon = (
              <Icon iconName="X" className="gcl-gems-design-modification-list-not-released-icon gcl-ml-xs" />
            );

          return (
            <>
              {link}
              {statusIcon}
            </>
          );
        },
      ),
      createTableColumn({
        accessor: loadingRemovalCertSearchColumnKeys.ACTIONER,
        Header: 'Actioner',
        Cell: createTableCell(
          (value: string, item: ILoadingRemovalCertSearch, index: number, cellExtras: { [key: string]: any }) => {
            const className = getT4ActionerClassName(item);

            return (
              <div className={className ? `gcl-gems-permit-to-load-remove-cell ${className}` : ''}>
                {item.actioner === null || item.actioner === '' ? '-' : item.actioner}
              </div>
            );
          },
        ),
      } as Column),
      createTableDefaultColumn(loadingRemovalCertSearchColumnKeys.RISK_CATEGORY, 'Risk Cat.'),
      createTableLinkWithOpenNewTabColumn<ILoadingRemovalCertSearch>(
        loadingRemovalCertSearchColumnKeys.T1_REF_NO,
        'T1 Ref. No.',
        ({ item }) => generateRoutePath(routes.t1.view.path, { id: item.projectMasterSchedule?.id.toString() }),
        ({ value, item, index, cellExtras }, link) => link,
      ),
      createTableDefaultColumn(loadingRemovalCertSearchColumnKeys.DESIGN_PACKAGE, 'Design Package'),
      createTableDefaultColumn(loadingRemovalCertSearchColumnKeys.LOCATION, 'Location'),
      createTableDefaultColumn(loadingRemovalCertSearchColumnKeys.ELEMENT_OF_WORKS, 'Element of works'),
      {
        accessor: loadingRemovalCertSearchColumnKeys.REMOVAL_REQUIRED,
        Header: 'Removal required?',
        disableResizing: true,
        disableSortBy: false,
        Cell: createTableCell<boolean, object>(
          (value: boolean | undefined, item: object, index: number, cellExtras: { [key: string]: any }) => {
            if (value)
              return <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" />;
            if (value === undefined || value === null) return <Icon iconName="Dash" />;
            return <Icon iconName="RecordCircle" className="gcl-gems-permit-to-load-remove-list-not-released-icon " />;
          },
        ),
      },
      createTableUserRoleColumn(loadingRemovalCertSearchColumnKeys.TWC, 'TWC/TWCM'),
      createTableUserRoleColumn(loadingRemovalCertSearchColumnKeys.SRP, 'SRP'),
    ];
  }, []);
  // #endregion Table Columns

  // #region Handle load cert and removal cert
  const [LoadCert, setLoadCert] = React.useState<ILoadingRemovalCertSearch[]>([]);
  const [RemovalCert, setRemovalCert] = React.useState<{ [key: string]: ILoadingRemovalCertSearch[] }>({});
  React.useEffect(() => {
    const tempLoadCert: ILoadingRemovalCertSearch[] = [];
    const tempRemovalCert: { [key: string]: ILoadingRemovalCertSearch[] } = {};

    designBriefPagedResult?.items?.forEach((item: ILoadingRemovalCertSearch) => {
      if (!item.removalCertRequired) {
        tempLoadCert.push(item);
        return;
      }
      if (!item.isRemovalCert && !item.hasRemovalCert) {
        tempLoadCert.push(item);
        return;
      }

      const headerKey = item.headerCertNo ?? '';
      const tempMapItem = tempRemovalCert[headerKey] || [];
      if (!item.isRemovalCert) tempLoadCert.push(item);
      if (item.isRemovalCert) tempRemovalCert[headerKey] = [...tempMapItem, item];
    });
    setLoadCert(tempLoadCert);
    setRemovalCert(tempRemovalCert);
  }, [JSON.stringify(designBriefPagedResult)]);
  // #endregion Handle load cert and removal cert

  const tableConfig: IResponsivePaginatedTableProps = {
    columns: permitToLoadRemoveTableColumns,
    paginationProps: {
      pageSizeOptions: DEFAULT_TABLE_PAGE_SIZE_OPTIONS,
      paginatorPosition: 'top',
      totalCount: designBriefPagedResult?.totalCount || 0,
      initialPageSize: initPageSize,
      onPageChange: async (pageIndex, newPageSize) => {
        setPageNumber(pageIndex + 1);
        setPageSize(newPageSize);
      },
    },
    sortingProps: {
      initialSortBy:
        sortByQuery && sortByQuery.sortByField
          ? [
              {
                id: sortByQuery.sortByField,
                desc: !!sortByQuery.isDesc,
              },
            ]
          : [],
      onSort: (sortBy) => {
        if (sortBy && sortBy.length >= 1) setSortByQuery({ sortByField: sortBy[0].id, isDesc: sortBy[0].desc });
        else setSortByQuery(undefined);
      },
    },
    allowSelectRow: false,
    items: [],
  };

  const shouldShowExpandTable = isXs
    ? false
    : !!(sortByQuery && sortByQuery.sortByField === DEFAULT_SORT_BY_FIELD_NAME);

  const tableLocal = React.useMemo(() => {
    if (shouldShowExpandTable)
      return (
        <ResponsivePaginatedTable
          {...tableConfig}
          tableCellsProps={{
            dataCell: {
              getColSpanByItem: (item: object | undefined, column: Column, cellExtras: TableCellExtras | undefined) => {
                const loadItem = item as ILoadingRemovalCertSearch;
                if (!loadItem.removalCertRequired) return undefined;

                if (column.id === 'expansion') return 1;

                if (column.Header === 'T4 No.') return undefined;

                return 1;
              },
            },
          }}
          items={LoadCert || []}
          allowExpandRow
          classNames={{
            header: 'gcl-gems-permit-to-load-remove-load',
            subRow: 'subrow',
            body: 'gcl-gems-list-table-body',
          }}
          rowExpansionProps={{
            defaultExpanded: true,
            getRowExpandItems: async (rowItem: object) => {
              const loadItem = rowItem as ILoadingRemovalCertSearch;
              if (!loadItem.removalCertRequired) return LoadCert || [];
              const headerKey = loadItem.headerCertNo ?? '';
              return RemovalCert[headerKey] || [];
            },
            checkRowCanExpand: (rowItem: object) => {
              const loadItem = rowItem as ILoadingRemovalCertSearch;
              let res = !!loadItem.removalCertRequired;
              if (!loadItem.isRemovalCert && !loadItem.hasRemovalCert) res = !!loadItem.isRemovalCert;
              return res;
            },
          }}
        />
      );
    return (
      <ResponsivePaginatedTable
        {...tableConfig}
        items={designBriefPagedResult?.items || []}
        allowExpandRow={false}
        classNames={{ body: 'gcl-gems-list-table-body' }}
      />
    );
  }, [shouldShowExpandTable, JSON.stringify(LoadCert), JSON.stringify(RemovalCert)]);

  return (
    <>
      <ApiErrorDisplay errors={[{ error: errorDesignBriefSearch, genericError: 'Failed to get T4 list' }]} />
      <SearchPageBase
        data={designBriefPagedResult?.items}
        breadcrumbItems={[
          {
            text: `${projectNo} - Home`,
            prefixIconName: 'House',
            onClick: () => {
              navigate(routes.t1.path);
            },
          },
          {
            text: `Search T4`,
            prefixIconName: 'Search' as IconName,
            active: true,
          },
        ]}
        title="T4 Permit to Load / Remove List"
        isTableRefetching={isRefetchingDesignBriefSearch}
        isTableLoading={isLoadingDesignBriefSearch}
        buttonNavBarItem={[
          ...(isAllowNewT4
            ? [
                {
                  text: 'New',
                  onClick: (e: React.MouseEvent | undefined) => {
                    openLoadingRemovalCertCreationModal(e);
                  },
                  iconName: 'FileEarmarkPlus' as IconName,
                  componentType: ComponentType.Primary,
                  showNoneInArchivedProject: true,
                },
              ]
            : ([] as IButtonNavBarItem[])),
          {
            text: 'Export',
            iconName: 'ArrowBarDown' as IconName,
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                exportFunction={exportProjectLoadingRemovalCertSearchExcelAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: loadingRemovalCertFilterObj,
                }}
              />
            ),
          },
          {
            text: 'Print',
            iconName: 'Printer' as IconName,
            componentType: ComponentType.Secondary,
            component: (
              <DownloadButton
                text="Print"
                prefixIconName="Printer"
                exportFunction={exportProjectLoadingRemovalCertSearchPdfAsync}
                defaultExportProps={{
                  projectId,
                  sortByQuery,
                  searchFilter: loadingRemovalCertFilterObj,
                }}
              />
            ),
          },
        ]}
        searchFilter={
          <SearchFilter
            fieldConfigs={permitToLoadRemoveSearchFieldConfigs}
            onFilterChanged={(filter) => {
              setLocalFilter(filter);
            }}
            defaultFilter={localFilter}
          />
        }
        appliedSearchFilter={localFilter}
        noContentLabel="No T4 created yet"
        onRenderTable={() => {
          return (
            <>
              <ApiErrorDisplay errors={[{ error: errorDesignBriefSearch, genericError: 'Failed to get T1 list' }]} />
              {tableLocal}
            </>
          );
        }}
        legend={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <b>T4 No.</b>
              <ul>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" />
                  <Icon iconName="RecordCircle" className="gcl-gems-permit-to-load-remove-list-not-released-icon" /> -
                  Construction Certificate Completed, Awaiting Removal Certificate
                </li>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" />
                  <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" /> -
                  Completed Form (Removal Cert Required)
                </li>
                <li>
                  <Icon iconName="RecordCircle" className="gcl-gems-permit-to-load-remove-list-not-released-icon" /> -
                  Incomplete Form
                </li>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" /> -
                  Completed Form (No Removal Cert Required)
                </li>
                <li>
                  <Icon iconName="X" className="gcl-gems-permit-to-load-remove-list-not-released-icon" /> - Cancelled
                  Form
                </li>
              </ul>
            </Grid>
            <Grid item xs={12} sm={6}>
              <b>Removal Required</b>
              <ul>
                <li>
                  <Icon iconName="CheckCircleFill" className="gcl-gems-permit-to-load-remove-list-released-icon" /> -
                  Yes
                </li>
                <li>
                  <Icon iconName="RecordCircle" className="gcl-gems-permit-to-load-remove-list-not-released-icon" /> -
                  No
                </li>
              </ul>
            </Grid>
          </Grid>
        }
      />
      {renderLoadingRemovalCertCreationModal()}
    </>
  );
};
